// LAYOUT DE TESTE — RETORNO POSICIONAL COM TIPO DE REGISTRO.
//
// Sintetico. Nao existe ente que mande este arquivo, e ele nunca sai do vitest:
// `situacao: "teste"` e' filtrada em toda listagem, em todo documento e em
// `layoutsAtribuiveis` (ver `catalogo.ts`). O que ele existe para fazer e' dar
// FIXTURE a nove valores da matriz de cenarios que Atlas e ALECE nao cobrem:
//
//   largura-fixa · abertura-detalhe-fechamento · fechamento-com-totais ·
//   centavos-implicitos · com-sinal · competencia-aamm · matricula-com-digito ·
//   sem-nome · reconhecido-por-conteudo
//
// (mais `latin1`, `crlf` e `linhas-vazias-no-fim`, que o exemplo tambem traz).
// Um valor sem exemplo que o exercite nao e' suportado — e' promessa, e
// `cobertura-matriz.test.ts` cobra isso layout a layout.
//
// TRES COISAS QUE ESTE ADAPTADOR FAZ E NENHUM OUTRO FAZIA:
//
// 1. RECONHECE PELO CONTEUDO. Nao ha nome padrao: o arquivo e' dele quando a
//    primeira linha util comeca com o registro `0` E toda linha util tem 120
//    posicoes. Reconhecer pelo nome recusaria um arquivo bem formado que o
//    operador renomeou, e aceitaria um relatorio qualquer renomeado para o
//    nome certo — os dois erros ja aconteceram em producao com outros layouts.
//
// 2. RECUSA POR TRAILER QUE NAO BATE. O fechamento declara quantidade e soma;
//    se o conteudo diz outra coisa, `reconhecer` devolve `false` carregando os
//    DOIS numeros (`esperado` e `encontrado`). Sem os dois, o ente nao tem como
//    saber qual lado esta errado — e "arquivo recusado" vira discussao.
//
// 3. DERIVA O NOME QUE O ARQUIVO NAO TEM. O registro de servidor nao traz nome,
//    e a carga do padrao exige um. O nome sai como `SERVIDOR <matricula>` e o
//    fato entra em `naoInformaveis`: e' informacao que a Atlas passa a NAO ter,
//    e o relatorio precisa dizer isso em vez de fingir que o campo veio.
import { montarLinhaDelimitada } from "../../csv-delimitado.js";
import { COLUNAS_CARGA, COLUNAS_CONSIGNADOS, RETORNO_SEPARADOR, SITUACAO_CARGA } from "../../retorno.js";
import { COLUNAS_RUBRICA } from "../../rubrica.js";
import { layoutDoCatalogo } from "../catalogo.js";
import { registrarExemplo } from "../exemplos.js";
import { lerCompetencia, lerData } from "../forma/data.js";
import { lerLarguraFixa } from "../forma/largura-fixa.js";
import { lerNumero } from "../forma/numero.js";
import { conferirTotais, exigirTotais, separarRegistros, } from "../forma/tipo-registro.js";
import { cpfCompletado, matriculaComDigitoValida } from "../identidade.js";
import { relatorioConversaoVazio } from "../relatorio.js";
import { TESTE_POSICIONAL_EXEMPLO } from "./posicional-exemplo.js";
import { CAMPOS_ABERTURA, CAMPOS_DESCONTO, CAMPOS_FECHAMENTO, CAMPOS_SERVIDOR, CENTAVOS, CENTAVOS_COM_SINAL, LARGURA_LINHA_POSICIONAL, QUANTIDADE, REGISTRO_DESCONTO, REGISTRO_SERVIDOR, TIPO_REGISTRO_POSICIONAL, } from "./posicional-colunas.js";
const CODIGO = "teste-posicional-retorno";
/** A competencia que o exemplo declara na abertura (`2609` → 202609). */
export const TESTE_POSICIONAL_COMPETENCIA = "202609";
/** O nome do arquivo do exemplo. E' ILUSTRATIVO: o reconhecimento nao o consulta. */
export const TESTE_POSICIONAL_NOME = "POSICIONAL-2609.TXT";
function versao() {
    return layoutDoCatalogo(CODIGO).versao;
}
/**
 * As linhas cruas do arquivo, sem interpretar campo nenhum.
 *
 * `forma/tabela.ts` nao serve aqui: ele quebra a linha em CELULAS, e num
 * arquivo posicional a linha inteira e' o registro. O que este leitor precisa
 * fazer e' so' o que o formato exige — tirar o BOM, aceitar CRLF e LF, e nao
 * comer a ultima quebra como se fosse uma linha vazia a mais.
 */
function linhasCruas(texto) {
    const limpo = (texto.charCodeAt(0) === 0xfeff ? texto.slice(1) : texto).replace(/\r\n?/g, "\n");
    const linhas = limpo.split("\n");
    if (linhas.length > 0 && linhas[linhas.length - 1] === "")
        linhas.pop();
    return linhas;
}
/** Linhas vazias no FIM — o que a matriz chama `linhas-vazias-no-fim`. */
function vaziasNoFim(linhas) {
    let n = 0;
    for (let i = linhas.length - 1; i >= 0 && linhas[i].trim() === ""; i--)
        n += 1;
    return n;
}
/**
 * Este arquivo e' deste layout?
 *
 * Duas perguntas, as duas sobre o CONTEUDO: a primeira linha util abre com o
 * registro `0`, e toda linha util tem a largura declarada. Juntas elas
 * distinguem o arquivo de qualquer CSV e de qualquer outro posicional com
 * largura diferente, que e' tudo o que o reconhecimento precisa fazer aqui.
 */
function ehDesteLayout(linhas) {
    const uteis = linhas.filter((l) => l.trim() !== "");
    if (uteis.length < 3)
        return false;
    if (uteis[0][0] !== TIPO_REGISTRO_POSICIONAL.abertura)
        return false;
    return uteis.every((l) => l.length === LARGURA_LINHA_POSICIONAL);
}
/** O primeiro arquivo de TEXTO do pacote que passa pelo teste de conteudo. */
function acharCandidato(arquivos) {
    for (const arquivo of arquivos) {
        if (arquivo.origem === "planilha")
            continue;
        const linhas = linhasCruas(arquivo.texto);
        if (!ehDesteLayout(linhas))
            continue;
        return { arquivo, linhas, registros: separarRegistros(linhas, TIPO_REGISTRO_POSICIONAL) };
    }
    return null;
}
/** O que o conteudo soma: quantidade de detalhes e soma dos descontos. */
function apurar(registros) {
    let soma = 0;
    for (const linha of registros.detalhes) {
        if (linha[0] !== REGISTRO_DESCONTO)
            continue;
        const { valor } = lerLarguraFixa(linha, CAMPOS_DESCONTO);
        // Valor ilegivel deixa a soma diferente da declarada de proposito: o
        // fechamento e' quem denuncia, e a recusa mostra os dois numeros.
        soma += lerNumero(valor, CENTAVOS_COM_SINAL) ?? 0;
    }
    return { quantidade: registros.detalhes.length, soma: Math.round(soma * 100) / 100 };
}
/** O que o fechamento declara. `null` quando a propria linha esta ilegivel. */
function totaisDoFechamento(fechamento) {
    const f = lerLarguraFixa(fechamento, CAMPOS_FECHAMENTO);
    const quantidade = lerNumero(f.quantidade, QUANTIDADE);
    const soma = lerNumero(f.soma, CENTAVOS_COM_SINAL);
    if (quantidade === null || soma === null)
        return null;
    return { quantidade, soma };
}
/** A conferencia do trailer contra o conteudo, ou `null` se o trailer nao se le. */
function conferir(registros) {
    if (registros.fechamento === null)
        return null;
    const esperado = totaisDoFechamento(registros.fechamento);
    if (esperado === null)
        return null;
    return conferirTotais({ esperado, encontrado: apurar(registros) });
}
function arredondar(n) {
    return Math.round(n * 100) / 100;
}
function juntar(linhas) {
    return linhas.length === 0 ? "" : `${linhas.join("\r\n")}\r\n`;
}
export const adaptadorTestePosicionalRetorno = {
    codigo: CODIGO,
    reconhecer(arquivos) {
        const candidato = acharCandidato(arquivos);
        if (!candidato) {
            return {
                reconhecido: false,
                layout: CODIGO,
                encontrados: [],
                faltando: ["retorno"],
                motivo: "nenhum arquivo do envio começa com o registro de abertura `0` e tem " +
                    `${LARGURA_LINHA_POSICIONAL} posições em toda linha — este layout é reconhecido pelo conteúdo, não pelo nome`,
            };
        }
        const encontrados = [candidato.arquivo.nome];
        const { registros } = candidato;
        if (registros.abertura === null) {
            return {
                reconhecido: false,
                layout: CODIGO,
                encontrados,
                faltando: ["abertura"],
                motivo: "o arquivo não tem a linha de abertura (registro `0`)",
            };
        }
        if (registros.fechamento === null) {
            return {
                reconhecido: false,
                layout: CODIGO,
                encontrados,
                faltando: ["fechamento"],
                motivo: "o arquivo não tem a linha de fechamento (registro `9`)",
            };
        }
        const conferencia = conferir(registros);
        if (conferencia === null) {
            return {
                reconhecido: false,
                layout: CODIGO,
                encontrados,
                faltando: ["fechamento"],
                motivo: "a linha de fechamento (registro `9`) não traz quantidade e soma legíveis",
            };
        }
        if (!conferencia.ok) {
            // O C8 da conformidade: a recusa leva os DOIS lados, e e' isso que a
            // borda mostra em "trailer adulterado".
            return {
                reconhecido: false,
                layout: CODIGO,
                encontrados,
                faltando: [],
                motivo: `o fechamento não confere com o conteúdo do arquivo — ${conferencia.divergencias.join("; ")}`,
                esperado: conferencia.esperado,
                encontrado: conferencia.encontrado,
            };
        }
        return { reconhecido: true, layout: CODIGO, encontrados, faltando: [] };
    },
    traduzir(arquivos, ctx) {
        const candidato = acharCandidato(arquivos);
        // Pre-condicao, nao conteudo: a borda chama `reconhecer` antes.
        if (!candidato)
            throw new Error("teste-posicional-retorno: nenhum arquivo do envio tem a forma deste layout");
        const { registros } = candidato;
        if (registros.abertura === null || registros.fechamento === null) {
            throw new Error("teste-posicional-retorno: arquivo sem abertura ou sem fechamento");
        }
        const rel = relatorioConversaoVazio({ codigo: CODIGO, versao: versao() }, ctx.competencia);
        // ---- Abertura: a competencia do ARQUIVO tem de ser a da folha. Traduzir
        // um arquivo de agosto para a folha de setembro produziria desconto de um
        // mes na margem de outro, e nada no resultado denunciaria a mistura.
        const abertura = lerLarguraFixa(registros.abertura, CAMPOS_ABERTURA);
        const competenciaDoArquivo = lerCompetencia(abertura.competencia, "aamm");
        if (competenciaDoArquivo === null) {
            throw new Error(`teste-posicional-retorno: a abertura não traz competência AAMM legível ("${abertura.competencia}")`);
        }
        if (competenciaDoArquivo !== ctx.competencia) {
            throw new Error(`teste-posicional-retorno: o arquivo é da competência ${competenciaDoArquivo} e esta folha é ${ctx.competencia}`);
        }
        // ---- Fechamento: recusa antes de gerar linha nenhuma.
        const conferencia = conferir(registros);
        if (conferencia === null) {
            throw new Error("teste-posicional-retorno: a linha de fechamento não traz quantidade e soma legíveis");
        }
        exigirTotais(conferencia);
        rel.avisos.push(`folha ${abertura.codigoFolha || "(sem código)"}, competência ${competenciaDoArquivo} declarada na abertura`);
        const vazias = vaziasNoFim(candidato.linhas);
        if (vazias > 0)
            rel.avisos.push(`${vazias} linha(s) vazia(s) no fim do arquivo, ignoradas`);
        for (const f of registros.fora) {
            rel.descartadas.push({ arquivo: "posicional", linha: f.linha, motivo: f.motivo });
        }
        const linhasServidor = registros.detalhes.filter((l) => l[0] === REGISTRO_SERVIDOR);
        const linhasDesconto = registros.detalhes.filter((l) => l[0] === REGISTRO_DESCONTO);
        rel.lidos = [
            { arquivo: "servidor", linhas: linhasServidor.length },
            { arquivo: "desconto", linhas: linhasDesconto.length },
        ];
        const carga = montarCarga(linhasServidor, ctx, rel);
        const descontos = montarDescontos(linhasDesconto, ctx, rel);
        rel.gerados = [
            { arquivo: "carga", linhas: carga.linhas.length },
            { arquivo: "consignados", linhas: descontos.consignados.length },
            { arquivo: "rubrica", linhas: descontos.rubricas.length },
        ];
        if (carga.linhas.length > 0) {
            rel.normalizados.push({
                campo: "salarioLiquido",
                regra: "centavos inteiros do arquivo convertidos para reais",
                quantidade: carga.linhas.length,
            });
        }
        if (descontos.consignados.length > 0) {
            rel.normalizados.push({
                campo: "valor",
                regra: "centavos inteiros com sinal na última posição convertidos para reais",
                quantidade: descontos.consignados.length,
            });
        }
        // O campo que o arquivo NAO tem. Derivar `SERVIDOR <matricula>` mantem a
        // carga valida sem inventar pessoa — mas quem le o relatorio precisa saber
        // que aquele nome nao veio do ente.
        rel.naoInformaveis.push({
            campo: "nome",
            motivo: "o registro de servidor não traz o nome; a carga recebe `SERVIDOR <matrícula>`",
            linhasAfetadas: carga.linhas.length,
        });
        rel.totais.origem = {
            fechamento_quantidade: conferencia.esperado.quantidade ?? 0,
            fechamento_soma: conferencia.esperado.soma ?? 0,
            detalhe_quantidade: conferencia.encontrado.quantidade ?? 0,
            detalhe_soma: conferencia.encontrado.soma ?? 0,
        };
        rel.totais.gerado = {
            carga_salario: arredondar(carga.totalSalario),
            consignados_valor: arredondar(descontos.consignados.reduce((s, c) => s + c.valor, 0)),
            rubrica_valor: arredondar(descontos.rubricas.reduce((s, r) => s + r.valor, 0)),
        };
        return {
            pacote: {
                carga: juntar(carga.linhas),
                consignados: juntar(descontos.consignados.map((c) => c.linha)),
                rubricas: juntar(descontos.rubricas.map((r) => r.linha)),
            },
            relatorio: rel,
        };
    },
};
// ============================================================ partes
function montarCarga(linhas, ctx, rel) {
    const saida = [];
    const vistas = new Set();
    let totalSalario = 0;
    linhas.forEach((bruta, i) => {
        const linha = i + 1;
        const r = lerLarguraFixa(bruta, CAMPOS_SERVIDOR);
        const matricula = (r.matricula ?? "").trim();
        const descartar = (motivo) => rel.descartadas.push({ arquivo: "servidor", linha, matricula, motivo });
        if (matricula === "")
            return descartar("matrícula vazia");
        // O digito verificador e' o que este layout existe para exercitar: sem ele,
        // uma matricula com um digito trocado entraria como servidor novo.
        if (!matriculaComDigitoValida(matricula))
            return descartar(`matrícula "${matricula}" não fecha no dígito verificador`);
        if (vistas.has(matricula))
            return descartar("matrícula repetida no arquivo");
        const cpf = cpfCompletado(r.cpf ?? "");
        if (!cpf)
            return descartar(`CPF "${(r.cpf ?? "").trim()}" não confere no dígito verificador`);
        const situacao = (r.situacao ?? "").trim().toUpperCase();
        if (!SITUACAO_CARGA.includes(situacao)) {
            return descartar(`situação "${situacao}" não é um código do padrão Atlas`);
        }
        const salario = lerNumero(r.salario ?? "", CENTAVOS);
        if (salario === null)
            return descartar(`salário "${(r.salario ?? "").trim()}" não é um número em centavos`);
        const admissao = lerData(r.admissao ?? "", "ddmmaaaa");
        if ((r.admissao ?? "").trim() !== "" && admissao === null) {
            rel.avisos.push(`matrícula ${matricula}: data de admissão "${r.admissao}" ilegível, campo vazio na carga`);
        }
        vistas.add(matricula);
        totalSalario += salario;
        saida.push(montarLinhaDelimitada(COLUNAS_CARGA, {
            competencia: ctx.competencia,
            matricula,
            cpf: cpf.cpf,
            // O arquivo nao tem nome. `naoInformaveis` registra o preco.
            nome: `SERVIDOR ${matricula}`,
            situacao,
            vinculo: (r.vinculo ?? "").trim(),
            cargo: (r.cargo ?? "").trim(),
            // A carga do padrao escreve DDMMAAAA; `lerData` devolveu ISO.
            dataAdmissao: admissao === null ? "" : `${admissao.slice(8, 10)}${admissao.slice(5, 7)}${admissao.slice(0, 4)}`,
            salarioLiquido: salario,
            bancoCodigo: (r.banco ?? "").trim(),
            agencia: (r.agencia ?? "").trim(),
            conta: (r.conta ?? "").trim(),
        }, RETORNO_SEPARADOR));
    });
    return { linhas: saida, totalSalario };
}
function montarDescontos(linhas, ctx, rel) {
    const consignados = [];
    const rubricas = [];
    linhas.forEach((bruta, i) => {
        const linha = i + 1;
        const r = lerLarguraFixa(bruta, CAMPOS_DESCONTO);
        const matricula = (r.matricula ?? "").trim();
        const verba = (r.verba ?? "").trim();
        const descartar = (motivo) => rel.descartadas.push({ arquivo: "desconto", linha, matricula, motivo });
        if (matricula === "" || verba === "")
            return descartar("matrícula ou verba vazia");
        const valor = lerNumero(r.valor ?? "", CENTAVOS_COM_SINAL);
        if (valor === null)
            return descartar(`valor "${(r.valor ?? "").trim()}" não é um número em centavos com sinal`);
        if (valor <= 0) {
            // Para a Atlas, linha AUSENTE do consignados significa "não descontada".
            // Emitir a linha com zero faria o domínio ler "desconto parcial de zero",
            // que é outra coisa.
            return descartar("valor zerado ou negativo — para a Atlas, a linha ausente já significa não descontada");
        }
        const descricao = (r.descricao ?? "").trim();
        const contrato = (r.contrato ?? "").trim();
        const parcela = (r.parcela ?? "").trim();
        const parcelasTotal = (r.parcelasTotal ?? "").trim();
        consignados.push({
            linha: montarLinhaDelimitada(COLUNAS_CONSIGNADOS, {
                matricula,
                codigoVerba: verba,
                descricao,
                tipo: "D",
                valor,
                parcelasRestantes: "",
                referencia: contrato,
                parcelaAtual: parcela,
                parcelasTotal,
            }, RETORNO_SEPARADOR),
            valor,
        });
        rubricas.push({
            linha: montarLinhaDelimitada(COLUNAS_RUBRICA, { competencia: ctx.competencia, matricula, codigoVerba: verba, descricao, tipo: "D", valor }, RETORNO_SEPARADOR),
            valor,
        });
    });
    return { consignados, rubricas };
}
registrarExemplo(CODIGO, () => ({
    descricao: "Um arquivo posicional Latin-1 com abertura, quatro servidores, cinco descontos e fechamento conferido — " +
        "e duas linhas vazias no fim.",
    competencia: TESTE_POSICIONAL_COMPETENCIA,
    arquivos: [{ nome: TESTE_POSICIONAL_NOME, texto: TESTE_POSICIONAL_EXEMPLO }],
}));
//# sourceMappingURL=posicional-retorno.js.map